/**
 * How many decisions wait for the signed-in person (ops.actions.inboxCount),
 * for the header's count chip, and a toast when that number grows.
 *
 * The count is read when the console loads, on every navigation, when the
 * window regains focus and after every decision made in this tab (the case
 * page's and the inbox's decision calls announce APPROVAL_DECIDED_EVENT).
 * There is no interval: the read rides useVisibilityRefresh with no timer,
 * so a tab left open costs nothing until the person comes back to it.
 *
 * A read that returns more than the previous read in this page session shows
 * "<n> new decision(s) waiting" through the console's toast, linking to the
 * inbox. The first read never toasts: what was already waiting is the chip's
 * job, the toast is for what arrived while the person was here.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import useVisibilityRefresh from '../../../platform/hooks/useVisibilityRefresh'
import { useToast } from '../../../platform/components/ToastProvider'
import { opsCall } from '../../../platform/services/mcpClient'
import { APPROVAL_DECIDED_EVENT } from './approvalDecided'

export const APPROVALS_INBOX_PATH = '/investigate/approvals'

/** The Pending tab's size, as one number; null when the gateway sent none. */
export async function fetchApprovalsInboxCount() {
  const out = await opsCall(null, 'actions', 'inboxCount')
  if (out?.error) throw new Error(out.error.message || String(out.error))
  return Number.isInteger(out?.pending) && out.pending >= 0 ? out.pending : null
}

/** "1 new decision waiting" / "3 new decisions waiting". */
export function newDecisionsLine(added) {
  return `${added} new ${added === 1 ? 'decision' : 'decisions'} waiting`
}

export default function useApprovalsInbox({ enabled = true } = {}) {
  const location = useLocation()
  const toast = useToast()
  const [pending, setPending] = useState(null)
  // The previous read of this page session: the toast compares against it,
  // so only growth since the person arrived is announced.
  const lastReadRef = useRef(null)

  const refresh = useCallback(async ({ signal } = {}) => {
    const count = await fetchApprovalsInboxCount()
    if (signal?.aborted || count === null) return
    const previous = lastReadRef.current
    lastReadRef.current = count
    setPending(count)
    if (previous !== null && count > previous) {
      toast.info(
        <Link className="ops-toast-link" to={APPROVALS_INBOX_PATH} data-testid="approvals-new-toast">
          {newDecisionsLine(count - previous)}
        </Link>,
      )
    }
  }, [toast])

  // Mount, every navigation (the location key changes on each one) and
  // window focus come from the shared refresh hook; intervalMs null means no
  // timer is ever armed.
  const refreshNow = useVisibilityRefresh({ refresh, enabled, intervalMs: null, resetKey: location.key })

  useEffect(() => {
    if (!enabled) return undefined
    const onDecided = () => { void refreshNow().catch(() => {}) }
    window.addEventListener(APPROVAL_DECIDED_EVENT, onDecided)
    return () => window.removeEventListener(APPROVAL_DECIDED_EVENT, onDecided)
  }, [enabled, refreshNow])

  return { pending, refresh: refreshNow }
}
